import React, { FormEvent, Fragment, useEffect, useState } from 'react'
import { Cell, Form } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'

import type { Ingredient, IngredientGroup, LanguagesConfig } from '@/api/types'
import { Button } from '@/components/Button'
import { Label } from '@/components/Label'
import { PageEditHeader } from '@/components/PageEditHeader'
import { Column, Row, Table, TableBody, TableHeader } from '@/components/Table'
import { TextField } from '@/components/TextField'
import FCDBIngredientSelector from './FCDBIngredientSelector'

import './EditIngredientGroup.css'
import Languages from '../Languages/Languages'

type Props = {
  ingredientGroup: IngredientGroup
  languages: LanguagesConfig
  saveGroup(group: IngredientGroup): void
  deleteGroup?(id: number): void
}

export default function EditIngredientGroup({
  ingredientGroup,
  languages,
  saveGroup,
  deleteGroup,
}: Props) {
  const { t } = useTranslation()
  const [tempGroup, setTempGroup] = useState(ingredientGroup)

  useEffect(() => {
    setTempGroup(ingredientGroup)
  }, [ingredientGroup])

  const handleChangeInput = (key: string, value: string) => {
    setTempGroup({
      ...tempGroup,
      [key]: value,
    })
  }

  const handleChangeTranslation = (languageCode: string, translation: string) => {
    if (languages && languages.defaultLanguage !== languageCode) {
      if (!tempGroup.nameTranslations) {
        tempGroup.nameTranslations = []
      }

      const translationIndex = tempGroup.nameTranslations.findIndex(
        (t) => t.languageCode === languageCode,
      )
      if (translationIndex !== -1) {
        tempGroup.nameTranslations[translationIndex].translation = translation
      } else {
        tempGroup.nameTranslations.push({ languageCode, translation })
      }
    } else {
      tempGroup.name = translation
    }

    setTempGroup({
      ...tempGroup,
    })
  }

  const deleteFCDBIngredient = (id: number) => {
    const updatedList = tempGroup.ingredients.filter((rt) => rt.idIngredient !== id)

    setTempGroup({
      ...tempGroup,
      ingredients: updatedList,
    })
  }

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault()
    saveGroup(tempGroup)
  }

  const handleDeleteGroup = () => {
    if (deleteGroup && ingredientGroup.idIngredientGroup) {
      deleteGroup(ingredientGroup.idIngredientGroup)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <PageEditHeader title={t('ingredientgroup.editIngredientGroup')}>
        {ingredientGroup.idIngredientGroup && (
          <Button variant="negative" onPress={handleDeleteGroup}>
            {t('ingredient.delete')}
          </Button>
        )}
        <Button variant="primary" type="submit">
          {ingredientGroup.idIngredientGroup
            ? t('ingredient.saveIngredient')
            : t('ingredient.createIngredient')}
        </Button>
      </PageEditHeader>

      <div className="panel">
        {languages && languages.languages.length > 0 ? (
          <Languages
            languages={languages}
            object={tempGroup}
            handleChangeTranslation={handleChangeTranslation}
            errorMessage={t('ingredient.emptyName')}
          />
        ) : (
          <TextField
            isRequired
            label={t('ingredient.name')}
            value={tempGroup.name}
            errorMessage={t('ingredient.emptyName')}
            onChange={(value) => handleChangeInput('name', value)}
          />
        )}
        <div className="EditIngredientGroup-ingredients">
          <div className="EditIngredientGroup-add">
            <Label>{t('ingredient.fcdbIngredients')}</Label>

            <FCDBIngredientSelector
              selection={tempGroup.ingredients}
              onChangeSelection={(selection) => {
                setTempGroup({
                  ...tempGroup,
                  ingredients: selection as Ingredient[],
                })
              }}
            />
          </div>

          <Table aria-label="FCDB ingredients" style={{ padding: 0 }}>
            <TableHeader>
              <Column id="name" isRowHeader width="50%">
                {t('ingredient.name')}
              </Column>
              <Column id="englishName" isRowHeader width="50%">
                {t('ingredient.englishName')}
              </Column>
              <Column id="delete"></Column>
            </TableHeader>
            <TableBody
              items={tempGroup.ingredients}
              dependencies={[tempGroup]}
              renderEmptyState={() => <div>{t('ingredient.fcdbIngredientsEmpty')}</div>}>
              {(row) => (
                <Row id={row.idIngredient}>
                  <Cell>{row.name}</Cell>
                  <Cell>{row.englishName}</Cell>
                  <Cell>
                    <Button variant="icon" onPress={() => deleteFCDBIngredient(row.idIngredient)}>
                      <FontAwesomeIcon icon={faTrash} color="var(--invalid-color)" />
                    </Button>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Form>
  )
}
